import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Server, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);

    try {
      const user = await login(email, password);
      toast.success("Connexion reussie !");
      if (user.role === "admin") {
        navigate("/admin", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Une erreur est survenue";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090E] flex">
      {/* Left side */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1920&q=80')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#07090E] via-[#07090E]/70 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-end">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Supervisez toutes vos sauvegardes
          </h2>
          <p className="text-slate-400 text-lg max-w-md">
            Veeam, Iperius Backup et plus encore. Une seule plateforme pour surveiller vos sauvegardes clients.
          </p>
        </div>
      </div>

      {/* Right side */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
              <Server className="text-cyan-400" size={28} />
            </div>
            <div>
              <h1 className="font-bold text-2xl text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>ServeurOnline</h1>
              <p className="text-sm text-slate-500">Backup Monitoring SaaS</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800/60 bg-[#0D111A]/70 backdrop-blur-xl p-8">
            <h2 className="text-xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Connexion
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@entreprise.com"
                  required
                  className="bg-[#07090E] border-slate-700 text-white placeholder:text-slate-600 focus:border-cyan-500"
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Mot de passe</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="********"
                    required
                    className="bg-[#07090E] border-slate-700 text-white placeholder:text-slate-600 focus:border-cyan-500 pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold py-5 rounded-xl"
                data-testid="login-btn"
              >
                {isLoading ? "Connexion..." : "Se connecter"}
              </Button>
            </form>

            <div className="mt-6 space-y-3 text-center">
              <Link
                to="/forgot-password"
                className="block text-sm text-slate-400 hover:text-cyan-400 transition-colors"
                data-testid="forgot-password-link"
              >
                Mot de passe oublie ?
              </Link>
              <Link
                to="/register"
                className="block text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                Pas de compte ? Essai gratuit 14 jours
              </Link>
            </div>
          </div>

          <div className="mt-6 flex justify-center gap-6 text-sm">
            <Link to="/" className="text-slate-500 hover:text-slate-300">Accueil</Link>
            <Link to="/pricing" className="text-slate-500 hover:text-slate-300">Tarifs</Link>
            <Link to="/contact" className="text-slate-500 hover:text-slate-300">Contact</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
